import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PhotoViewerModal from '../components/PhotoViewerModal';
import { PhotoViewerItem } from '../utils/photoMedia';

export interface OpenPhotoViewerOptions {
  title?: string;
  photos?: PhotoViewerItem[];
  objetId?: string;
  startIndex?: number;
}

interface PhotoViewerContextValue {
  openPhotoViewer: (options: OpenPhotoViewerOptions) => void;
  closePhotoViewer: () => void;
}

const PhotoViewerContext = createContext<PhotoViewerContextValue | null>(null);

export const PhotoViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewerState, setViewerState] = useState<{
    open: boolean;
    title?: string;
    photos: PhotoViewerItem[];
    objetId?: string;
    startIndex: number;
  }>({ open: false, photos: [], startIndex: 0 });

  const openPhotoViewer = useCallback((options: OpenPhotoViewerOptions) => {
    setViewerState({
      open: true,
      title: options.title,
      photos: options.photos ?? [],
      objetId: options.objetId,
      startIndex: options.startIndex ?? 0,
    });
  }, []);

  const closePhotoViewer = useCallback(() => {
    setViewerState((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({ openPhotoViewer, closePhotoViewer }),
    [openPhotoViewer, closePhotoViewer],
  );

  return (
    <PhotoViewerContext.Provider value={value}>
      {children}
      {viewerState.open && (
        <PhotoViewerModal
          title={viewerState.title}
          initialPhotos={viewerState.photos}
          objetId={viewerState.objetId}
          startIndex={viewerState.startIndex}
          onClose={closePhotoViewer}
        />
      )}
    </PhotoViewerContext.Provider>
  );
};

export function usePhotoViewer(): PhotoViewerContextValue {
  const ctx = useContext(PhotoViewerContext);
  if (!ctx) {
    throw new Error('usePhotoViewer doit être utilisé dans PhotoViewerProvider');
  }
  return ctx;
}
